"use client";

import { useState } from "react";
import { unstable_isUnrecognizedActionError } from "next/navigation";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { track } from "@/lib/analytics/tracker";
import { navigateToDocument, reloadDocument } from "@/lib/document-navigation";
import { describeError } from "@/lib/handle-error";
import { reportSignInConversion, type SignInSurface } from "@/lib/auth/sign-in/conversion";
import type { SignedIn, SignInResult } from "@/lib/auth/sign-in/result";
import type { AuthEntryPoint, AuthMethod } from "@/lib/analytics/types";
import {
  trackAuthCompleted,
  trackAuthFailure,
  type AuthAttempt,
} from "@/lib/auth/sign-in/auth-tracking";

const FAILED_MESSAGE = "We couldn't sign you in. Please try again.";

function hostedSignInUrl(): string {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  return `/sign-in?${new URLSearchParams({ returnTo }).toString()}`;
}

function reportUnfinished(
  attempt: AuthAttempt,
  result: Extract<SignInResult, { status: "error" | "needs_hosted" }>,
  setError: (message: string) => void,
): void {
  if (result.status === "needs_hosted") {
    trackAuthFailure(attempt, "verify", "needs_hosted");
    navigateToDocument(hostedSignInUrl());
    return;
  }
  trackAuthFailure(attempt, "verify", result.reason);
  setError(result.message);
}

export function useAuthCompletion(options: {
  entryPoint: AuthEntryPoint;
  surface: SignInSurface;
  method: AuthMethod;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
  onNeedsCode?: () => void;
}) {
  const reloadSignedIn = reloadDocument;
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const attempt: AuthAttempt = { entryPoint: options.entryPoint, method: options.method };

  const complete = async (result: SignedIn) => {
    trackAuthCompleted(attempt, result.isNew);
    const refreshed = await refreshAuth().catch(() => ({ error: "refresh failed" }));
    if (refreshed?.error) {
      reloadSignedIn();
      return;
    }
    if (options.surface !== "password") {
      track("auth.code_verified", { entry_point: options.entryPoint, is_new: result.isNew });
    }
    reportSignInConversion(result.isNew, options.surface);
    await options.onSignedIn(result);
  };

  const run = async (signInAttempt: () => Promise<SignInResult>) => {
    setIsBusy(true);
    setError(null);
    try {
      const result = await signInAttempt();
      if (result.status === "signed_in") await complete(result);
      else if (result.status === "needs_code") options.onNeedsCode?.();
      else reportUnfinished(attempt, result, setError);
    } catch (cause) {
      if (unstable_isUnrecognizedActionError(cause)) {
        reloadDocument();
        return;
      }
      trackAuthFailure(attempt, "verify", "exception");
      setError(describeError(cause, FAILED_MESSAGE));
    } finally {
      setIsBusy(false);
    }
  };

  return { error, setError, clearError: () => setError(null), isBusy, run };
}
